import { PageLoader, useMinLoading } from "@/components/Preloader";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Radio, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Article, CATEGORY_ROUTES } from "@/lib/types";
import { ArticleCard } from "@/components/ArticleCard";
import { NewsletterCTA } from "@/components/NewsletterCTA";
import { Button } from "@/components/ui/button";

const SPOTLIGHTS: Record<string, { name: string; desc: string; accent: string; art: string }> = {
  ai: {
    name: "Artificial Intelligence",
    desc: "AI agents, models, compute networks and onchain AI — where machine intelligence meets the onchain economy.",
    accent: "#f04dff",
    art: "/portals2/ai.jpg",
  },
  quantum: {
    name: "Quantum",
    desc: "Quantum computing, post-quantum cryptography and the infrastructure securing the next era of digital assets.",
    accent: "#05d1e5",
    art: "/portals2/quantum.jpg",
  },
};

const SpotlightHero = ({ slug, total }: { slug: string; total?: number }) => {
  const spot = SPOTLIGHTS[slug];
  const { data: live } = useQuery({
    queryKey: ["spotlight-live", slug],
    queryFn: async () => (await api.get(`/sectors/${slug}/spotlight`)).data,
    staleTime: 2 * 60 * 1000,
  });
  return (
    <header className="relative overflow-hidden rounded-2xl border bg-[#020308] mb-10"
      style={{ borderColor: `${spot.accent}33` }} data-testid={`sector-spotlight-${slug}`}>
      <div className="absolute inset-y-0 right-0 w-full md:w-[62%]" aria-hidden>
        <img src={spot.art} alt="" className="h-full w-full object-cover object-[50%_35%] opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020308] via-[#020308]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020308]/70 via-transparent to-transparent" />
      </div>
      <div className="absolute inset-0 md:hidden bg-[#020308]/55" aria-hidden />
      <div className="absolute inset-x-0 top-0 h-px" aria-hidden
        style={{ background: `linear-gradient(90deg, ${spot.accent}, transparent 70%)` }} />
      <div className="relative max-w-2xl px-6 py-12 sm:px-10 sm:py-16">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] mb-3"
          style={{ color: spot.accent }}>
          <span className="h-1.5 w-1.5 rotate-45" style={{ backgroundColor: spot.accent }} aria-hidden />
          Sector Spotlight
        </p>
        <h1 className="font-heading font-black text-white text-3xl sm:text-5xl leading-tight">{spot.name}</h1>
        <p className="text-gray-300 text-sm sm:text-base leading-relaxed mt-4 max-w-xl">{spot.desc}</p>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          {live?.stories_7d > 0 ? (
            <span data-testid="spotlight-week-pulse"
              className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-black/40 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-widest text-gray-300 backdrop-blur-sm">
              <span className="relative flex h-2 w-2" aria-hidden>
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                  style={{ backgroundColor: spot.accent }} />
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: spot.accent }} />
              </span>
              {live.stories_7d} {live.stories_7d === 1 ? "story" : "stories"} this week
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-black/40 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-widest text-gray-300 backdrop-blur-sm">
              <Radio className="h-3.5 w-3.5" style={{ color: spot.accent }} /> Live coverage
            </span>
          )}
          {live?.top_topic && (
            <span data-testid="spotlight-trending"
              className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-widest backdrop-blur-sm"
              style={{ borderColor: `${spot.accent}4d`, color: spot.accent, backgroundColor: `${spot.accent}14` }}>
              <TrendingUp className="h-3.5 w-3.5" /> Trending: {live.top_topic}
            </span>
          )}
          {typeof total === "number" && total > 0 && (
            <span data-testid="spotlight-story-count"
              className="inline-flex items-center rounded-full border border-white/12 bg-black/40 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-widest text-gray-300 backdrop-blur-sm">
              {total} {total === 1 ? "story" : "stories"} tracked
            </span>
          )}
        </div>
      </div>
    </header>
  );
};

export default function CategoryPage() {
  const location = useLocation();
  const slug = location.pathname.replace("/", "");
  const isLatest = slug === "latest";
  const meta = CATEGORY_ROUTES.find((c) => c.slug === slug);
  const [page, setPage] = useState(1);

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const catInfo = (categories || []).find((c: any) => c.slug === slug);

  const { data, isLoading } = useQuery({
    queryKey: ["articles", slug, page],
    queryFn: async () => {
      const params: any = { page, limit: 12 };
      if (!isLatest) params.category = slug;
      return (await api.get("/articles", { params })).data;
    },
  });

  const [accumulated, setAccumulated] = useState<Article[]>([]);
  const items: Article[] = page === 1 ? data?.items || [] : [...accumulated, ...(data?.items || [])];
  const showLoader = useMinLoading(isLoading, 700, slug);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid={`category-page-${slug}`}>
      {SPOTLIGHTS[slug] ? (
        <SpotlightHero slug={slug} total={data?.total} />
      ) : (
        <header className="mb-10 border-b border-white/10 pb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-[#00E5FF] mb-2">Section</p>
          <h1 className="font-heading font-black text-white text-3xl sm:text-5xl">
            {isLatest ? "Latest" : meta?.name || catInfo?.name || slug}
          </h1>
          {catInfo?.description && <p className="text-gray-400 mt-3 max-w-2xl">{catInfo.description}</p>}
          {isLatest && <p className="text-gray-400 mt-3">Every story, the moment it breaks.</p>}
        </header>
      )}
      {showLoader && page === 1 ? (
        <PageLoader />
      ) : items.length === 0 ? (
        <p className="text-gray-500 py-16 text-center">No stories in this section yet — check back shortly.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
          {data && page < data.pages && (
            <div className="text-center mt-10">
              <Button data-testid="load-more-button" variant="outline"
                className="border-white/20 bg-transparent text-white hover:bg-white/5"
                onClick={() => { setAccumulated(items); setPage(page + 1); }}>
                Load more stories
              </Button>
            </div>
          )}
        </>
      )}

      <div className="mt-16">
        <NewsletterCTA sourceType={`category_${slug}`} interests={isLatest ? [] : [slug]}
          heading={`Never miss a ${isLatest ? "story" : (meta?.name || slug) + " story"}.`} />
      </div>
    </div>
  );
}
